import {
  QueryObserverOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

import {
  MessageFeedbackFormRequest,
  MessageFeedbackRequest,
  MessageRequest,
  MessageResponse,
  sessionApi,
} from '@/services/sessions';
import { Session } from '@/types/chat';

const SESSIONS_STALE_TIME = 10000; // 10 seconds
const SESSIONS_GC_TIME = 2 * 60 * 1000; // 2 minutes

export const SESSIONS_QUERY_OPTIONS = {
  staleTime: SESSIONS_STALE_TIME,
  gcTime: SESSIONS_GC_TIME,
};

export const GET_DOCUMENT_SESSION_HISTORY_QUERY_KEY =
  'get_document_session_history';
export const GET_QUOTE_SESSION_HISTORY_QUERY_KEY = 'get_quote_session_history';
export const GET_QUOTE_SESSIONS_QUERY_KEY = 'get_quote_sessions';
export const GET_CONTACT_SESSIONS_QUERY_KEY = 'get_contact_sessions';
export const GET_DOCUMENT_SESSIONS_QUERY_KEY = 'get_document_sessions';
export const GET_CONNIE_SESSIONS_QUERY_KEY = 'get_connie_sessions';
export const GET_CONNIE_SESSION_HISTORY_QUERY_KEY =
  'get_connie_session_history';

type SessionsFetcher<TParams> = (
  params: TParams
) => Promise<Session[] | undefined>;

const useSessionsQuery = <TParams>(
  queryKey: string,
  fetcher: SessionsFetcher<TParams>,
  params: TParams,
  options?: Partial<QueryObserverOptions<Session[] | undefined>>
) =>
  useQuery({
    queryKey: [queryKey, params],
    queryFn: () => fetcher(params),
    ...SESSIONS_QUERY_OPTIONS,
    ...options,
  });

export function useGetDocumentSessions(
  {
    docId,
  }: {
    docId: string;
  },
  options?: Partial<QueryObserverOptions<Session[] | undefined>>
) {
  return useSessionsQuery(
    GET_DOCUMENT_SESSIONS_QUERY_KEY,
    sessionApi.getDocumentSessions,
    { doc_id: docId },
    options
  );
}
export function useGetContactSessions(
  {
    contactId,
  }: {
    contactId: string;
  },
  options?: Partial<QueryObserverOptions<Session[] | undefined>>
) {
  return useSessionsQuery(
    GET_CONTACT_SESSIONS_QUERY_KEY,
    sessionApi.getContactSessions,
    { contact_id: contactId },
    options
  );
}

export function useGetConnieSessions(
  { userId, contactId }: { userId: string; contactId?: string },
  options?: Partial<QueryObserverOptions<Session[] | undefined>>
) {
  return useSessionsQuery(
    GET_CONNIE_SESSIONS_QUERY_KEY,
    sessionApi.getConnieSessions,
    {
      user_id: userId,
      ...(contactId ? { contact_id: contactId } : {}),
    },
    options
  );
}

export function useGetQuoteSessions(
  { quoteId }: { quoteId: string },
  options?: Partial<QueryObserverOptions<Session[] | undefined>>
) {
  return useSessionsQuery(
    GET_QUOTE_SESSIONS_QUERY_KEY,
    sessionApi.getQuoteSessions,
    { quote_id: quoteId },
    options
  );
}

const useMessageMutation = <TData, TResult>(
  mutationFn: (data: TData) => Promise<TResult>
) =>
  useMutation<TResult, Error, TData>({
    mutationFn,
  });

export function useSendChatMessage(
  mutationFn: (data: MessageRequest) => Promise<MessageResponse | undefined>
) {
  return useMessageMutation<MessageRequest, MessageResponse | undefined>(
    mutationFn
  );
}

export function useSendFeedbackMessage() {
  return useMessageMutation<MessageFeedbackRequest, void>(
    sessionApi.sendFeedbackMessage
  );
}

export function useSendFeedback() {
  return useMutation({
    mutationFn: (data: MessageFeedbackFormRequest) =>
      sessionApi.sendFeedback(data),
  });
}
